import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Link2, Copy, TrendingUp, Eye, ShoppingCart,
  Wallet, LogOut, Clock, CheckCircle, Award, ChevronRight, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('affiliate_token');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('affiliate_token');
        localStorage.removeItem('affiliate_user');
        navigate('/connexion-partenaire');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate('/connexion-partenaire'); return; }
    fetchData();
  }, [token, navigate, fetchData]);

  const logout = () => {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_user');
    navigate('/connexion-partenaire');
  };

  const copyLink = () => {
    if (!data) return;
    navigator.clipboard.writeText(`${window.location.origin}/r/${data.slug}`);
    toast.success('Lien copié !');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (!data) return null;

  const pendingCommission = data.total_commission_earned - data.total_commission_paid;
  const isPending = data.status === 'pending';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex justify-between items-center max-w-4xl mx-auto">
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-bold text-foreground truncate" data-testid="affiliate-dashboard-title">
              Espace Partenaire
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {data.prenom} {data.nom}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="flex-shrink-0"
            data-testid="affiliate-logout"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Déconnexion</span>
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Pending account alert */}
        {isPending && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="pending-alert">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-amber-900">Compte en attente de validation</p>
                <p className="text-xs text-amber-800 mt-1">
                  Votre inscription est en cours de vérification. Votre lien de parrainage sera activé
                  dès la validation par notre équipe (24-48h).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Personal Link */}
        <Card className="p-4 sm:p-6 mb-6" data-testid="affiliate-link-card">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-foreground text-sm sm:text-base">Votre lien personnel</h2>
            {data.status === 'active' && (
              <Badge className="bg-green-100 text-green-800 text-xs">Actif</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-secondary/50 border border-border rounded-lg px-3 py-2.5 min-w-0">
              <p className="text-sm font-mono truncate" data-testid="affiliate-link-value">
                {window.location.origin}/r/{data.slug}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={copyLink}
              className="flex-shrink-0 h-10"
              data-testid="copy-affiliate-link"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Copier</span>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Partagez ce lien. Chaque client qui commande via ce lien vous génère une commission.
          </p>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card className="p-3 sm:p-4 text-center">
            <Eye className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
            <p className="text-xl sm:text-2xl font-bold text-foreground">{data.total_referrals}</p>
            <p className="text-xs text-muted-foreground">Clics</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <ShoppingCart className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
            <p className="text-xl sm:text-2xl font-bold text-foreground">{data.total_conversions}</p>
            <p className="text-xs text-muted-foreground">Ventes</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <TrendingUp className="w-5 h-5 text-accent mx-auto mb-2" />
            <p className="text-xl sm:text-2xl font-bold text-accent">
              CHF {data.total_commission_earned.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground">Commissions</p>
          </Card>
          <Card className="p-3 sm:p-4 text-center">
            <Wallet className="w-5 h-5 text-primary mx-auto mb-2" />
            <p className="text-xl sm:text-2xl font-bold text-primary">
              CHF {pendingCommission.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground">En attente</p>
          </Card>
        </div>

        {/* Commission rate info */}
        <Card className="p-4 sm:p-5 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Votre taux de commission</p>
              <p className="text-xs text-muted-foreground mt-0.5">Appliqué sur chaque vente réalisée via votre lien</p>
            </div>
            <Badge variant="outline" className="text-lg font-bold px-3 py-1">
              {data.commission_rate}%
            </Badge>
          </div>
        </Card>

        {/* Commission Plan */}
        {data.tiers && (
          <Card className="p-4 sm:p-6 mb-6" data-testid="commission-plan-card">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-foreground text-sm sm:text-base">Plan de rémunération</h2>
            </div>

            {/* Progress toward next tier */}
            {data.next_tier && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 sm:p-4 mb-5" data-testid="next-tier-progress">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs sm:text-sm font-medium text-foreground">
                    Prochain palier : <span className="text-primary font-bold">{data.next_tier.name}</span> ({data.next_tier.rate}%)
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {data.next_tier.sales_remaining} vente{data.next_tier.sales_remaining > 1 ? 's' : ''} restante{data.next_tier.sales_remaining > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2.5">
                  <div
                    className="bg-primary h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${data.progress_pct}%` }}
                    data-testid="tier-progress-bar"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {data.total_conversions} / {data.next_tier.min_sales} ventes
                </p>
              </div>
            )}
            {!data.next_tier && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 mb-5 flex items-center gap-2" data-testid="max-tier-reached">
                <Star className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm font-medium text-amber-900">
                  Vous avez atteint le palier maximum — félicitations !
                </p>
              </div>
            )}

            {/* Tiers grid */}
            <div className="space-y-2" data-testid="tiers-list">
              {data.tiers.map((tier, idx) => {
                const isCurrent = data.current_tier && data.current_tier.name === tier.name;
                const isReached = data.total_conversions >= tier.min_sales;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      isCurrent
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : isReached
                        ? 'border-green-200 bg-green-50/50'
                        : 'border-border bg-card'
                    }`}
                    data-testid={`tier-${tier.name.toLowerCase()}`}
                  >
                    {/* Tier indicator */}
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCurrent ? 'bg-primary text-white' : isReached ? 'bg-green-100 text-green-700' : 'bg-secondary text-muted-foreground'
                    }`}>
                      {isReached ? (
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                      ) : (
                        <span className="text-xs font-bold">{idx + 1}</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-semibold ${isCurrent ? 'text-primary' : isReached ? 'text-green-800' : 'text-foreground'}`}>
                          {tier.name}
                        </p>
                        {isCurrent && (
                          <Badge className="bg-primary text-white text-[10px] px-1.5 py-0">Actuel</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {tier.max_sales ? `${tier.min_sales} à ${tier.max_sales} ventes` : `${tier.min_sales}+ ventes`}
                      </p>
                    </div>

                    {/* Rate */}
                    <div className={`text-right flex-shrink-0 ${isCurrent ? 'text-primary' : isReached ? 'text-green-700' : 'text-muted-foreground'}`}>
                      <p className="text-lg sm:text-xl font-bold">{tier.rate}%</p>
                    </div>

                    {/* Arrow if not current */}
                    {!isCurrent && !isReached && (
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 hidden sm:block" />
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground mt-4 text-center">
              Votre commission augmente automatiquement à chaque nouveau palier atteint.
            </p>
          </Card>
        )}

        {/* Recent referrals */}
        <Card className="p-4 sm:p-6" data-testid="referrals-list">
          <h2 className="font-semibold text-foreground text-sm sm:text-base mb-4">
            Historique des commissions
          </h2>
          {data.referrals.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucune commission pour le moment</p>
              <p className="text-xs text-muted-foreground mt-1">
                Partagez votre lien pour commencer à gagner des commissions
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.referrals.slice().reverse().map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      Vente — CHF {ref.payment_amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(ref.date).toLocaleDateString('fr-CH')} · {ref.commission_rate}%
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold text-accent">
                      +CHF {ref.commission.toFixed(2)}
                    </span>
                    {ref.status === 'paid' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
